import mixins from "$src/styles/mixins.module.css";
import { cx } from "@gotracksuit/design";
import { FloatingPortal } from "@floating-ui/react";
import { AnimatePresence, motion } from "motion/react";
import { ComponentProps, type CSSProperties, type Ref } from "react";

import styles from "./nav-menu.module.css";

export type NavMenuProps = {
  className?: string;
  open: boolean;
  children: React.ReactNode;
  constrained?: boolean;
  fixedWidth?: boolean;
  align?: "left" | "right" | "center";
  /** Render the menu through a portal so it escapes scroll/overflow ancestors */
  portal?: boolean;
  /** Inline style passthrough — used by the caller to apply floating-ui positioning */
  style?: CSSProperties;
  /** Ref forwarded to the menu's positioning wrapper (used by floating-ui in portal mode) */
  ref?: Ref<HTMLDivElement>;
} & Omit<ComponentProps<"div">, "children" | "style" | "ref">;

export const NavMenu = ({
  open,
  children,
  constrained,
  fixedWidth,
  align,
  className,
  portal,
  style,
  ref,
}: NavMenuProps) => {
  const menuClasses = cx(
    styles["nav-menu"],
    constrained && styles.constrained,
    constrained && mixins.scrollable,
    fixedWidth && styles["fixed-width"],
    align === "left" && styles.openLeft,
    align === "right" && styles.openRight,
    portal && styles.portal,
    className,
  );

  const inner = (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="menu"
          className={menuClasses}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 0 }}
          transition={{ duration: 0.1 }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (portal) {
    return (
      <FloatingPortal>
        <div ref={ref} style={style} className={styles.portalWrap}>
          {inner}
        </div>
      </FloatingPortal>
    );
  }

  return (
    <div ref={ref} style={style}>
      {inner}
    </div>
  );
};
